import { useMemo, useState } from "react";
import type { AssuranceMode } from "@veto/domain";
import type { ProfileMedia } from "@veto/protocol";
import { AvatarPicker } from "../components/AvatarPicker";
import { peekPendingInviteName } from "../messaging/pendingInvite";

/** Passcode strength against OFFLINE guessing (a copied device vault). */
export function passcodeStrength(pin: string): { level: 0 | 1 | 2 | 3; label: string } {
  if (pin.length < 6) return { level: 0, label: "Too short — at least 6 characters" };
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pin)).length;
  const digitsOnly = /^\d+$/.test(pin);
  if (digitsOnly && pin.length < 10) return { level: 1, label: "Weak — a short number can be guessed if someone copies your device" };
  if (pin.length >= 12 || (pin.length >= 10 && classes >= 3)) return { level: 3, label: "Strong" };
  return { level: 2, label: "Good — longer is stronger" };
}

export function Onboarding({
  onComplete,
}: {
  onComplete: (input: {
    displayName: string;
    pin: string;
    assuranceMode: AssuranceMode;
    avatar?: ProfileMedia | null;
  }) => Promise<void>;
}) {
  const [step, setStep] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [avatar, setAvatar] = useState<ProfileMedia | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inviter = useMemo(() => peekPendingInviteName(), []);
  const strength = passcodeStrength(pin);
  const mismatch = pin2.length > 0 && pin2 !== pin;
  const canFinish = displayName.trim().length >= 1 && strength.level >= 1 && pin === pin2 && !busy;

  async function finish() {
    if (!canFinish) return;
    setBusy(true);
    setError(null);
    try {
      await onComplete({ displayName: displayName.trim(), pin, assuranceMode: "pwa", avatar });
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? `Setup failed: ${e.message}`
          : "Setup failed — this browser may block storage (private mode?). Try a normal window.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="onboarding">
      <div className="card stack">
        <div className="step-dots" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span key={i} className={i === step ? "active" : ""} />
          ))}
        </div>

        {step === 0 && (
          <>
            <div className="brand-lockup">
              <div className="brand-mark">V</div>
              <h1 className="brand-wordmark">
                <em>V</em>eto
              </h1>
            </div>
            {inviter && (
              <p className="invite-hello" role="status">
                <strong>{inviter}</strong> invited you to chat. Set up takes about 30 seconds.
              </p>
            )}
            <p>Private messaging with no phone number and no email. Your keys are created on this device and never leave it.</p>
            <ul className="feature-list">
              <li>End-to-end encrypted, with post-quantum protection</li>
              <li>Delivered through many independent relays — no single server to trust</li>
              <li>Add people with a link or QR code</li>
            </ul>
            <button className="btn" autoFocus onClick={() => setStep(1)}>
              Create identity
            </button>
          </>
        )}

        {step === 1 && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (displayName.trim()) setStep(2);
            }}
          >
            <h2>What should people call you?</h2>
            <div className="onboarding-avatar-row">
              <AvatarPicker
                name={displayName}
                value={avatar}
                onChange={(m) => {
                  setAvatar(m);
                  setAvatarError(null);
                }}
                size={84}
                onError={(msg) => setAvatarError(msg)}
              />
              <div className="stack">
                <span className="muted text-sm">Photo (optional)</span>
                {avatarError && (
                  <span className="muted text-sm" role="alert">
                    {avatarError}
                  </span>
                )}
              </div>
            </div>
            <div className="field">
              <label htmlFor="displayName">Your name</label>
              <input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoFocus
                autoComplete="nickname"
                enterKeyHint="next"
                maxLength={64}
                placeholder="e.g. Alex"
              />
              <p className="muted text-sm" style={{ margin: 0 }}>
                Shown only to people you chat with — sent encrypted, never to a server.
              </p>
            </div>
            <button className="btn" type="submit" disabled={!displayName.trim()}>
              Continue
            </button>
            <button className="btn secondary" type="button" onClick={() => setStep(0)}>
              Back
            </button>
          </form>
        )}

        {step === 2 && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void finish();
            }}
          >
            <h2>Lock Veto on this device</h2>
            <p className="muted" style={{ margin: 0 }}>
              Your passcode encrypts your keys and messages here. Choose something you won&apos;t forget —
              <strong> there is no reset</strong>, not even by us.
            </p>
            <div className="field">
              <label htmlFor="pin">Passcode</label>
              <div className="pin-row">
                <input
                  id="pin"
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  enterKeyHint="next"
                  autoFocus
                  aria-describedby="pin-strength"
                />
                <button type="button" className="btn ghost small" onClick={() => setShowPin((v) => !v)}>
                  {showPin ? "Hide" : "Show"}
                </button>
              </div>
              <div className={`strength strength-${strength.level}`} aria-hidden>
                <span />
                <span />
                <span />
              </div>
              <p id="pin-strength" className="muted text-sm" style={{ margin: 0 }}>
                {pin ? strength.label : "6+ characters. A few random words is strongest."}
              </p>
            </div>
            <div className="field">
              <label htmlFor="pinConfirm">Repeat passcode</label>
              <input
                id="pinConfirm"
                type={showPin ? "text" : "password"}
                value={pin2}
                onChange={(e) => setPin2(e.target.value)}
                autoComplete="new-password"
                enterKeyHint="done"
                aria-invalid={mismatch}
              />
              {mismatch && (
                <p className="field-error" role="alert">
                  Passcodes don&apos;t match
                </p>
              )}
            </div>
            <p className="muted text-sm" style={{ margin: 0 }}>
              You&apos;re using the web app. Keys stay encrypted in this browser; the iOS/Android app adds
              hardware-backed protection.
            </p>
            {error && (
              <p className="field-error" role="alert">
                {error}
              </p>
            )}
            <button className="btn" type="submit" disabled={!canFinish}>
              {busy ? "Creating your keys…" : "Generate keys & start"}
            </button>
            <button className="btn secondary" type="button" onClick={() => setStep(1)} disabled={busy}>
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
